import { useCallback, useState } from "react";
import { Button, Empty, Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDumbbell, faPaste, faPlus } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "../../common/StrictModeDroppable";
import { ExerciseEntry } from "../../types/Programmazione";
import { useClipboard } from "../../contexts/clipboard";
import { EntityOptionsState, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import type { Option } from "../../types/generics";
import ExerciseCardView from "./ExerciseCardView";
import ExerciseIdentityModal, { type ExerciseIdentity } from "./ExerciseIdentityModal";

type Props = {
  exercises: ExerciseEntry[];
  splitIndex: number;
  canEdit: boolean;
  isTargetAthlete: boolean;
  entityOpts: EntityOptionsState;
  progEditorOptions?: ProgEditorOptions;
  mutations: ReturnType<typeof useProgrammazioneMutations>;
  exercisesOptions: Option[];
  muscleGroupOptions: Option[];
};

export default function ExerciseListView({
  exercises,
  splitIndex,
  canEdit,
  isTargetAthlete,
  entityOpts,
  progEditorOptions,
  mutations,
  exercisesOptions,
  muscleGroupOptions,
}: Props) {
  const { copiedExercise } = useClipboard();
  const [addOpen, setAddOpen] = useState(false);

  // PROG-DETAIL identity flow — the "Aggiungi Esercizio" button now opens the
  // identity modal in add mode (instead of pushing a blank custom exercise),
  // so the trainer picks Tipo + Gruppo Muscolare + Esercizio up front.
  const handleAddConfirm = useCallback(
    (identity: ExerciseIdentity) => {
      const entry: ExerciseEntry = {
        type: identity.type,
        muscleGroupId: identity.muscleGroupId,
        exerciseId: identity.type === "catalog" ? identity.exerciseId : undefined,
        customName:
          identity.type === "custom"
            ? identity.customName
            : exercisesOptions.find(
                (opt) => opt.value?.id === identity.exerciseId,
              )?.value?.name,
      };
      mutations.addExerciseFull(splitIndex, entry);
      setAddOpen(false);
    },
    [exercisesOptions, mutations, splitIndex],
  );

  const handleExerciseDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;
      mutations.reorderExercises(splitIndex, result.source.index, result.destination.index);
    },
    [mutations, splitIndex]
  );

  const renderCard = (exercise: ExerciseEntry, exIndex: number, dragHandleProps?: any) => (
    <ExerciseCardView
      key={`exercise-${splitIndex}-${exIndex}`}
      exercise={exercise}
      exIndex={exIndex}
      splitIndex={splitIndex}
      canEdit={canEdit}
      isTargetAthlete={isTargetAthlete}
      entityOpts={entityOpts}
      progEditorOptions={progEditorOptions}
      mutations={mutations}
      exercisesOptions={exercisesOptions}
      muscleGroupOptions={muscleGroupOptions}
      dragHandleProps={dragHandleProps}
      onDeleteExercise={() => mutations.deleteExercise(splitIndex, exIndex)}
      onPasteExercise={
        copiedExercise
          ? () => mutations.pasteExercise(splitIndex, copiedExercise)
          : undefined
      }
    />
  );

  return (
    <>
      {exercises.length > 0 ? (
        canEdit ? (
          <DragDropContext onDragEnd={handleExerciseDragEnd}>
            <StrictModeDroppable droppableId={`view-exercises-${splitIndex}`}>
              {(provided) => (
                <div className="exercises-list" ref={provided.innerRef} {...provided.droppableProps}>
                  {exercises.map((exercise, exIndex) => (
                    <Draggable
                      key={`view-exercise-${splitIndex}-${exIndex}`}
                      draggableId={`view-exercise-${splitIndex}-${exIndex}`}
                      index={exIndex}
                    >
                      {(dragProvided) => (
                        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                          {renderCard(exercise, exIndex, dragProvided.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        ) : (
          <div className="exercises-list">
            {exercises.map((exercise, exIndex) => renderCard(exercise, exIndex))}
          </div>
        )
      ) : (
        // PROG-DETAIL/IMPL J1 — empty state uses AntD Empty with a dumbbell
        // icon instead of a bare <p>, so it reads as an empty surface and
        // not a missing-data error.
        <Empty
          image={
            <FontAwesomeIcon
              icon={faDumbbell}
              style={{ fontSize: 28, color: "var(--app-gray-400)" }}
            />
          }
          description={
            <span style={{ color: "var(--app-gray-600)", fontSize: 13 }}>
              Nessun esercizio in questo split
            </span>
          }
          style={{ margin: "12px 0" }}
        />
      )}

      {canEdit && (
        <Flex gap={8} className="mt-2" wrap="wrap">
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setAddOpen(true)}
            style={{ flex: 1 }}
          >
            Aggiungi Esercizio
          </Button>
          {copiedExercise && (
            <Button
              icon={<FontAwesomeIcon icon={faPaste} />}
              onClick={() => mutations.pasteExercise(splitIndex, copiedExercise)}
              style={{ flex: 1 }}
            >
              Incolla Esercizio
            </Button>
          )}
        </Flex>
      )}

      {canEdit && (
        <ExerciseIdentityModal
          open={addOpen}
          mode="add"
          initial={{ type: "custom" }}
          exercisesOptions={exercisesOptions}
          muscleGroupOptions={muscleGroupOptions}
          onCancel={() => setAddOpen(false)}
          onConfirm={handleAddConfirm}
        />
      )}
    </>
  );
}
